import { Component, inject, NgZone, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalStorageService } from '../core/services/local-storage.service';
import { JsBridgeService } from '../core/services/js-bridge.service';
import { PushNotificationService } from '../core/services/push-notification.service';
import { AuthService } from '../core/services/auth.service';
import { StartupOrchestratorService } from '../core/services/startup-orchestrator.service';
import { ConfigurationControllerService } from '../api-clients/generated/services/AnmcoBackend/api/configurationController.service';
import { Configurazione } from '../api-clients/generated/services/AnmcoBackend/model/configurazione';
import { PushContent } from '../core/models/push-content.model';
import { FileContent } from '../core/models/file-content.model';
import { StorageProperties } from '../core/enums/storage-properties.enum';

const SPLASH_MIN_DELAY_MS = 2000;

function dismissSplash(): void {
  const splash = document.getElementById('app-splash');
  if (!splash) return;
  splash.classList.add('fade-out');
  setTimeout(() => splash.remove(), 400);
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {

  private pushService = inject(PushNotificationService);
  private authService = inject(AuthService);
  private orchestrator = inject(StartupOrchestratorService);
  private configController = inject(ConfigurationControllerService);

  constructor(
    private zone: NgZone,
    private localStorageService: LocalStorageService,
    private jsBridgeService: JsBridgeService,
  ) {
    (window as any)['AppComponentRef'] = {
      zone: this.zone,
      component: this,
    };
  }

  async ngOnInit(): Promise<void> {
    const splashMinDelay = new Promise<void>(r => setTimeout(r, SPLASH_MIN_DELAY_MS));

    this.registerBridgeHandlers();

    if (this.authService.isAuthenticated()) {
      await this.orchestrator.runPostAuthChecks();
    }

    await splashMinDelay;
    dismissSplash();
  }

  private registerBridgeHandlers(): void {
    (window as any)['onPushReceived'] = (data: unknown) => {
      this.zone.run(() => {
        try {
          const raw = typeof data === 'string' ? JSON.parse(data) : data;
          const trigger = raw.trigger === 'foreground' || raw.trigger === 'tap'
            ? raw.trigger
            : undefined;
          const push: PushContent = {
            title: raw.title,
            body: raw.body ?? raw.message,
            data: raw.data ?? raw.extraData,
            trigger,
          };
          this.handlePush(push);
        } catch { /* invalid push data */ }
      });
    };

    (window as any)['onDeviceIdReceived'] = (deviceId: string) => {
      this.zone.run(() => {
        this.localStorageService.setDeviceId(deviceId);
      });
    };

    (window as any)['setAsMobilePlatform'] = (platform?: string) => {
      this.zone.run(() => {
        let resolved = platform;
        if (!resolved) {
          if (typeof (window as any).JSInterface !== 'undefined') {
            resolved = 'android';
          } else if (typeof (window as any).webkit?.messageHandlers !== 'undefined') {
            resolved = 'ios';
          } else {
            resolved = 'web';
          }
        }
        localStorage.setItem(StorageProperties.PLATFORM, resolved.toLowerCase());
      });
    };

    (window as any)['onQrCodeValueReceived'] = (value: string) => {
      this.zone.run(() => {
        this.jsBridgeService.emitQrCode(value);
      });
    };

    (window as any)['onFileReceived'] = (data: string) => {
      this.zone.run(() => {
        try {
          const file: FileContent = JSON.parse(data);
          this.handleFile(file);
        } catch { /* invalid file data */ }
      });
    };

    (window as any)['setVersionAndPlatform'] = (versionAndPlatform: { version: string; platform: string }) => {
      this.zone.run(() => {
        this.getConfig(versionAndPlatform);
      });
    };
  }

  private getConfig(versionAndPlatform: { version: string; platform: string }): void {
    this.configController.getConfiguration()
      .pipe(catchError(() => of({} as Configurazione)))
      .subscribe((resp) => {
        const platform = versionAndPlatform.platform?.toLowerCase();
        let reviewVersion: string | undefined;
        switch (platform) {
          case 'android':
            reviewVersion = resp.versioneInReviewAndroid;
            break;
          case 'ios':
            reviewVersion = resp.versioneInReviewIOS;
            break;
        }
        const isReview = reviewVersion !== undefined && versionAndPlatform.version === reviewVersion;
        this.localStorageService.isReviewMode = isReview;
      });
  }

  private handlePush(push: PushContent): void {
    this.pushService.handlePush(push);
    this.jsBridgeService.clearPushNotification();
  }

  private handleFile(file: FileContent): void {
    console.log('File received:', file);
  }
}
